// Refining (DESIGN §19, M1 scope §19.5): refineries at sites, crude intake (own fields routed + spot buys),
// batch/continuous stills with grade- and fire-test-dependent yields, fuel & opex, product tanks and sales
// (local, auto, export to Europe), lamp-explosion lawsuits & fire-test inspections, still fires / explosions
// (same warning → incident pattern as sim/safety.js), Standard Oil buy-out offers and the rebate squeeze.
//
// State (all JSON-safe):
//   state.refineries[uid] = { uid, name, siteId, fieldId, defId, units: [defId], pending: [{ defId, readyDay }],
//     throughputBbl, fireTest, crude: { penn, lima_sour, baku_heavy } (bbl), crudeCost ($ basis), crudeBbl (total),
//     products: { kerosene: gal, naphtha: gal, gasoline: gal, lube: gal, wax: lb, residuum: gal },
//     kero: { flash, skunk }, sell: { [pid]: { mode: 'auto'|'hold'|'export'|'dump', pct } }, autoBuy: { on, maxPrice, grade },
//     burnResiduum, safetyBudget, condition, downUntil, daysWithoutIncident, built, today: {...}, history: [[day, marginPerBbl]], stats }
//   state.productMarkets  (economy/products.js)
//   state.crudePurchases = [{ day, bbl, price, refineryUid, grade }]
//   state.refining = { routes: { [fieldId]: refineryUid }, shipments: [], squeeze: { level, untilDay, refusals },
//     standard: { offers: { [id]: offer }, lastOfferDay: { [uid]: day }, firstDone, sold }, shares: { value, startDay },
//     soldToday: { day, by: {} }, boughtToday: { day, bbl }, lampSuits, inspections }
import { chance, range, int, pick } from '../core/rng.js';
import { dayToDate, isFirstOfMonth } from '../core/time.js';
import { inflate } from '../data/cpi.js';
import { scriptedFlag } from '../data/news.js';
import {
  PRODUCTS, M1_PRODUCTS, REFINERY_UNITS, REFINERY_SITES, M1_SITES, CRUDE_GRADES, gradeForField, GRADE_PRICE_MULT, SKUNK_PRICE_MULT,
  FIRE_TEST, FIRE_TEST_SHIFT, fireTestPriceMult, lampRatePerGal, legalFireTest, LAMP_SUIT, INSPECTION, STANDARD_OIL,
  REFINERY_INCIDENTS, DUMPING_FINES, EXPORT_MIN_LOT_GAL, EXPORT_MIN_FIRE_TEST, EXPORT_LOSS_CHANCE, GAL_PER_BBL, WAX_LB_PER_GAL,
  REFINING_NEWS, refiningFlag, STILLMAN_WAGE_MULT, OVERHEAD_PER_DAY, BULK_SHARE,
  REFINED_PRODUCTS, productsAt, PRODUCT_FOLD, CONVERSION_CHAIN, MOTOR_GASOLINE_YEAR, STRAIGHT_RUN_OCTANE, OCTANE_STANDARD, TEL_BOOST,
  OCTANE_PRICE_SLOPE, OCTANE_MULT_RANGE, JET_SHARE, LAMP_ERA_MULT, PROCESS_SAFETY_MULT, DIESEL_SULFUR, EMISSIONS_COST, FIELD_SHIP_GAL, FIELD_SHIP_LINKED_GAL, MODERN_COST_MULT,
} from '../data/refining.js';
import { initProductMarkets, tickProductMarkets, productPrice, productPrices, productDepth, depthMult, freightIndex, exportPremium,
  oceanFreight, voyageDays, crackSpread, interp } from '../economy/products.js';
import { spotPrice } from '../economy/market.js';
import { fieldDef, barrelDef, unitPrice, eraDollars, yf, yearOf, marketWage } from './defs.js';
import { transportCapacity, transportCostPerBbl, royaltyRate, marketDepth } from './production.js';
import { incidentChance, budgetMitigation, handleDeath } from './safety.js';
import { makeCrew } from './crew.js';
import { queueDecision, popDecision } from './security.js';
import { clamp, notify, log, spend, earn, nextId, fmt$ } from './util.js';

// P&L categories used here (add to finance.PL_CATS; productSales & dividends are income).
export const PL = { sales: 'productSales', crude: 'crudePurchases', opex: 'refining', dividends: 'dividends' };
export const EXPORT_DEPTH_MULT = 4;          // Europe absorbs more kerosene than one US market
export const FIELD_SIDE_WAGON_GAL = 2500;    // products leave a field-side works by wagon unless rail/pipe reaches the field
export const RAIL_SHIP_GAL = 1e9;
export const AUTO_DUMP_FILL = 0.85;           // auto mode dumps unsellable naphtha/gasoline above this tank fill
const NO_PRODUCTS = () => ({ kerosene: 0, naphtha: 0, gasoline: 0, lube: 0, wax: 0, residuum: 0 });
const ZERO_ALL = () => ({ kerosene: 0, naphtha: 0, gasoline: 0, lube: 0, wax: 0, residuum: 0, diesel: 0, lpg: 0, asphalt: 0, jet: 0 });
const zeroOf = (list) => Object.fromEntries(list.map((p) => [p, 0]));
// Products this refinery stocks (M1 list before 1905; later cuts are added from their market year — normalizeRefinery).
export const plist = (ref) => REFINED_PRODUCTS.filter((p) => p in ref.products);
const NO_CRUDE = () => ({ penn: 0, lima_sour: 0, baku_heavy: 0 });
const r2 = (x) => Math.round(x * 100) / 100;

// ================================================================ init / helpers
export function initRefining(state) {
  state.refineries ??= {};
  state.crudePurchases ??= [];
  initProductMarkets(state);
  const r = (state.refining ??= {});
  r.routes ??= {};
  r.shipments ??= [];
  r.squeeze ??= { level: 0, untilDay: 0, refusals: 0 };
  r.standard ??= { offers: {}, lastOfferDay: {}, firstDone: false, sold: 0 };
  r.standard.offers ??= {};
  r.standard.lastOfferDay ??= {};
  r.shares ??= null;
  r.soldToday ??= { day: -1, by: {} };
  r.boughtToday ??= { day: -1, bbl: 0 };
  r.lampSuits ??= 0;
  r.inspections ??= 0;
  for (const ref of Object.values(state.refineries)) normalizeRefinery(ref, yearOf(state));
  return state;
}

function normalizeRefinery(ref, year = null) {
  ref.units ??= [];
  ref.pending ??= [];
  ref.crude = { ...NO_CRUDE(), ...(ref.crude ?? {}) };
  ref.products = { ...NO_PRODUCTS(), ...(ref.products ?? {}) };
  if (year != null) for (const p of productsAt(year)) ref.products[p] ??= 0;
  ref.kero ??= { flash: FIRE_TEST.default, skunk: 0 };
  if (year != null && year >= 1905) ref.gaso ??= { octane: STRAIGHT_RUN_OCTANE.penn };
  ref.sell ??= {};
  for (const p of plist(ref)) ref.sell[p] ??= defaultSell(p, year);
  ref.autoBuy ??= { on: false, maxPrice: 1, grade: null };
  ref.fireTest ??= FIRE_TEST.default;
  ref.burnResiduum ??= true;
  ref.safetyBudget ??= 0;
  ref.condition ??= 100;
  ref.downUntil ??= 0;
  ref.daysWithoutIncident ??= 0;
  ref.crudeCost ??= 0;
  ref.history ??= [];
  ref.stats ??= { bblRun: 0, revenue: 0, costs: 0, crudeBought: 0, dumpedGal: 0, exportedGal: 0 };
  ref.today ??= emptyToday(0);
  ref.crudeBbl = crudeTotal(ref);
  return ref;
}

function defaultSell(pid, year = null) {
  if (pid === 'gasoline' && !(year >= 1905)) return { mode: 'dump', pct: 100 };
  if (pid === 'residuum') return { mode: 'auto', pct: 100 };
  return { mode: 'auto', pct: 100 };
}

const emptyToday = (day, list = M1_PRODUCTS) => ({ day, run: 0, intake: 0, bought: 0, made: zeroOf(list), sold: zeroOf(list), revenue: 0, crudeCost: 0, opex: 0, fuel: 0, packaging: 0, license: 0, freight: 0, dumped: 0, margin: 0 });

export const unitDef = (id) => REFINERY_UNITS[id] ?? null;
export const siteDef = (id) => REFINERY_SITES[id] ?? null;
const crudeTotal = (ref) => Object.values(ref.crude).reduce((a, b) => a + b, 0);
const storedGal = (ref) => plist(ref).reduce((a, p) => a + (p === 'wax' ? ref.products[p] / WAX_LB_PER_GAL : ref.products[p]), 0);

export function unitPriceOf(state, unitId, siteId = null) {
  const u = unitDef(unitId);
  if (!u) return 0;
  const y = Math.min(yf(state), 2025);
  return inflate(u.basePrice, u.baseYear ?? u.year, Math.max(y, u.baseYear ?? u.year)) * (siteDef(siteId)?.landMult ?? 1);
}

export function unitAvailable(state, unitId) {
  const u = unitDef(unitId);
  if (!u) return false;
  return yf(state) >= u.year || !!state.research?.unlockedDefs?.[unitId];
}

function flagOn(state, flag) {
  const v = state.flags?.[flag];
  if (v !== undefined) return !!v;
  const rf = refiningFlag(flag, state.day);
  if (rf !== undefined) return !!rf;
  return !!scriptedFlag(flag, state.day);
}

const fi = (state) => freightIndex(yf(state));
const stillUnits = (ref) => ref.units.map(unitDef).filter((u) => u && u.kind === 'still');
const condFactor = (ref) => 0.5 + 0.5 * clamp(ref.condition, 0, 100) / 100;

export function refineryCapacity(ref) {
  return stillUnits(ref).reduce((a, u) => a + u.capacityBbl, 0) * condFactor(ref);
}
export function treatCapacity(ref) {
  return ref.units.map(unitDef).filter((u) => u?.kind === 'treater').reduce((a, u) => a + (u.treatBbl ?? 0), 0) * condFactor(ref);
}
export function crudeCapacity(ref) {
  return ref.units.map(unitDef).reduce((a, u) => a + (u?.crudeStorageBbl ?? 0), 0);
}
export function productCapacity(ref) {
  return ref.units.map(unitDef).reduce((a, u) => a + (u?.productStorageGal ?? 0), 0);
}

// Capacity-weighted yields of the still units for a crude grade, before the fire-test shift.
export function baseYields(ref, grade) {
  const st = stillUnits(ref);
  const tot = st.reduce((a, u) => a + u.capacityBbl, 0);
  const y = { ...ZERO_ALL(), loss: 0 };
  if (!tot) return y;
  for (const u of st) {
    const t = u.yields?.[grade] ?? u.yields?.penn;
    if (!t) continue;
    for (const k of Object.keys(y)) y[k] += (t[k] ?? 0) * u.capacityBbl / tot;
  }
  return y;
}

// Fire-test shift: below 110°F light naphtha is left in the kerosene (more gallons, lower flash);
// above it the kerosene is cut narrower (fewer gallons, safer, premium price). Sum is preserved.
export function applyFireTest(y, fireTest) {
  const out = { ...y };
  const s = clamp((FIRE_TEST.default - fireTest) / 40, -1, 1);
  if (s > 0) {
    const mv = Math.min(FIRE_TEST_SHIFT * s, out.naphtha * 0.8);
    out.kerosene += mv; out.naphtha -= mv;
  } else if (s < 0) {
    const mv = Math.min(FIRE_TEST_SHIFT * -s, out.kerosene * 0.5);
    out.kerosene -= mv; out.naphtha += mv;
  }
  return out;
}

export function yieldsFor(ref, grade, fireTest = ref.fireTest, run = null, year = null) {
  if (run == null && year == null) return applyFireTest(baseYields(ref, grade), fireTest);
  return processYields(ref, grade, fireTest, run, year).y;
}

// ---------------------------------------------------------------- the process chain (M2/M3)
// Straight-run barrel (primary stills, fire-test shift) → conversion units in CONVERSION_CHAIN order, each taking up to
// its capacity (as a share of today's crude run) of its feed cuts → jet split → cuts with no market yet folded back.
// Returns { y (fractions of crude), octane (gasoline pool RON), load: { unitId: feed bbl/day } }.
// With no conversion units before 1901 this is exactly the M1 yieldsFor().
export function convGroups(ref) {
  const n = {};
  for (const id of ref.units) if (unitDef(id)?.feed) n[id] = (n[id] ?? 0) + 1;
  const cf = condFactor(ref);
  return CONVERSION_CHAIN.filter((id) => n[id]).map((id) => ({ id, def: unitDef(id), n: n[id], cap: n[id] * unitDef(id).capacityBbl * cf }));
}
// Value of one unit of a cut for the feed decision: vgo sells as residuum; a cut with no market yet is worth what it folds to.
function cutValue(p, prices, year, octane = null, vgo = null) {
  if (p === 'loss') return 0;
  if (p === 'vgo') return vgo ?? prices.residuum ?? 0;
  if (p === 'wax') return (prices.wax ?? 0) * WAX_LB_PER_GAL;
  if (year != null && PRODUCTS[p] && year < PRODUCTS[p].fromYear) return PRODUCT_FOLD[p] === 'loss' ? 0 : cutValue(PRODUCT_FOLD[p], prices, year);
  const v = prices[p] ?? 0;
  return p === 'gasoline' && octane != null && year != null ? v * octanePriceMult(octane, year) : v;
}
export function processYields(ref, grade, fireTest = ref.fireTest, run = null, year = null, prices = null) {
  const y = applyFireTest(baseYields(ref, grade), fireTest);
  const sr = STRAIGHT_RUN_OCTANE[grade] ?? STRAIGHT_RUN_OCTANE.penn;
  let gv = y.gasoline, go = y.gasoline * sr;
  const load = {};
  const R = Math.max(1, run ?? Math.min(ref.throughputBbl ?? 0, refineryCapacity(ref) || ref.throughputBbl || 0));
  const groups = convGroups(ref);
  // heavy gas oil is worth what the best cracker on site makes of it (else it is sold as residuum)
  const outOf = (g, vgo) => Object.entries(g.def.out).reduce((a, [p, k]) => a + k * cutValue(p, prices, year, g.def.octane ?? 70, vgo), 0);
  const vgoV = prices ? Math.max(prices.residuum ?? 0, ...groups.filter((g) => g.def.feed.includes('vgo')).map((g) => outOf(g, null))) : 0;
  for (const g of groups) {
    const capFrac = g.cap / R;
    let done = 0;
    const outV = prices && !g.def.alwaysRun ? outOf(g, vgoV) : 0;
    for (const f of g.def.feed) {
      if (prices && !g.def.alwaysRun && outV <= cutValue(f, prices, year, f === 'gasoline' ? (gv > 1e-9 ? go / gv : sr) : null)) continue; // not worth cracking today
      const take = Math.min(y[f] ?? 0, capFrac - done);
      if (take <= 0) continue;
      if (f === 'gasoline') { go -= take * (gv > 1e-9 ? go / gv : sr); gv -= take; } // reformed out of the pool
      y[f] -= take; done += take;
    }
    if (done <= 0) continue;
    let outSum = 0;
    for (const [p, k] of Object.entries(g.def.out)) {
      y[p] = (y[p] ?? 0) + done * k; outSum += k;
      if (p === 'gasoline') { gv += done * k; go += done * k * (g.def.octane ?? 70); }
    }
    y.loss = (y.loss ?? 0) + done * Math.max(0, 1 - outSum);
    load[g.id] = done * R;
  }
  if (y.vgo) y.residuum += y.vgo;
  delete y.vgo;
  if (year != null) {
    const js = year >= 1952 ? interp(JET_SHARE, year) : 0;
    if (js > 0 && y.kerosene > 0) { const mv = y.kerosene * js; y.kerosene -= mv; y.jet = (y.jet ?? 0) + mv; }
    for (const [p, to] of Object.entries(PRODUCT_FOLD)) {
      if (!(y[p] > 0) || year >= PRODUCTS[p].fromYear) continue;
      y[to] = (y[to] ?? 0) + y[p]; y[p] = 0;
    }
  }
  return { y, octane: gv > 1e-9 ? go / gv : sr, load };
}
export const yieldsAt = (state, ref, grade, fireTest = ref.fireTest, run = null) => processYields(ref, grade, fireTest, run, yf(state), convGroups(ref).length ? productPrices(state) : null);

// Gasoline price multiplier for a pool octane vs the market's regular grade (+ lead), from 1912.
export function octanePriceMult(octane, year) {
  if (!(year >= MOTOR_GASOLINE_YEAR)) return 1;
  const k = 1 + OCTANE_PRICE_SLOPE * (octane + interp(TEL_BOOST, year) - interp(OCTANE_STANDARD, year));
  return clamp(k, OCTANE_MULT_RANGE[0], OCTANE_MULT_RANGE[1]);
}
export const octaneStandard = (year) => interp(OCTANE_STANDARD, year);
export const leadBoost = (year) => (year >= 1922 ? interp(TEL_BOOST, year) : 0);
const unitsOf = (ref, kind) => ref.units.map(unitDef).filter((u) => u?.kind === kind);
const hasAny = (ref, ids) => ids.some((id) => ref.units.includes(id));
const hydroCap = (ref) => ref.units.filter((id) => id === 'hydrotreater').length * (unitDef('hydrotreater')?.treatBbl ?? 0) * condFactor(ref);
// Low-sulfur diesel rule: the share of the diesel not covered by a hydrotreater sells at DIESEL_SULFUR.mult.
export function dieselSulfurMult(state, ref) {
  if (!(yf(state) >= DIESEL_SULFUR.fromYear)) return 1;
  const run = Math.max(1, Math.min(ref.throughputBbl ?? 0, refineryCapacity(ref) || 1));
  const cov = clamp(hydroCap(ref) / run, 0, 1);
  return 1 - (1 - DIESEL_SULFUR.mult) * (1 - cov);
}
// Productivity multiplier on operating costs after 1900 (exactly 1 before).
const costMult = (state) => { const y = yf(state); return y > 1900 ? interp(MODERN_COST_MULT, y) : 1; };
// Per-day operating cost of the conversion, treating and environmental units for a crude run (era $): { opex, fuel }.
function processCosts(state, ref, load, run) {
  let opex = 0, fuel = 0;
  for (const [id, bbl] of Object.entries(load)) {
    const u = unitDef(id);
    opex += bbl * eraDollars(state, u.opexPerBbl ?? 0, u.baseYear ?? u.year);
    fuel += bbl * eraDollars(state, u.fuelPerBbl ?? 0, u.baseYear ?? u.year);
  }
  const k = costMult(state);
  opex *= k; fuel *= k;
  const ht = Math.min(run, hydroCap(ref));
  if (ht > 0) { const u = unitDef('hydrotreater'); opex += ht * eraDollars(state, u.opexPerBbl, u.baseYear) * k; fuel += ht * eraDollars(state, u.fuelPerBbl, u.baseYear) * k; }
  const sru = ref.units.includes('sulfur_recovery');
  if (sru) { const u = unitDef('sulfur_recovery'); opex += run * eraDollars(state, u.opexPerBbl, u.baseYear) * k; }
  else if (yf(state) >= EMISSIONS_COST.fromYear && hasAny(ref, ['pipe_still', 'crude_unit'])) opex += run * eraDollars(state, EMISSIONS_COST.perBbl, EMISSIONS_COST.baseYear);
  return { opex, fuel };
}

// Static yields from a unit def (for previews / tests without a refinery).
export function unitYields(unitId, grade = 'penn', fireTest = FIRE_TEST.default) {
  const u = unitDef(unitId);
  const t = u?.yields?.[grade] ?? u?.yields?.penn;
  return t ? applyFireTest({ ...t }, fireTest) : null;
}

// ================================================================ sites, freight, squeeze, prices
// All domestic sites share one national market per product (Europe is separate).
function siteRegionKey() {
  return 'us';
}

export function squeezeState(state) {
  const sq = state.refining?.squeeze ?? { level: 0, untilDay: 0 };
  const active = sq.level > 0 && state.day < sq.untilDay;
  return { level: active ? sq.level : 0, untilDay: sq.untilDay, active, refusals: sq.refusals ?? 0 };
}

// Extra $/bbl on crude coming in by rail while Standard squeezes you (era $).
export function squeezeFreight(state, siteId) {
  const sq = squeezeState(state);
  const site = siteDef(siteId);
  if (!sq.active || !site?.rail) return 0;
  const relief = flagOn(state, 'tidewater') ? (site.pipelineRelief ?? 0) : 0;
  return eraDollars(state, STANDARD_OIL.freightAddPerBbl * sq.level * (site.squeeze ?? 1) * (1 - relief), 1872);
}

export function squeezeProductMult(state, siteId) {
  const sq = squeezeState(state);
  if (!sq.active) return 1;
  const site = siteDef(siteId);
  return 1 - STANDARD_OIL.productCutPerLevel * sq.level * (site?.rail ? 1 : 0.5);
}

// Price multiplier vs New York for selling a product at this site (discount shrinks as freight gets cheaper).
export function siteProductMult(state, siteId) {
  const site = siteDef(siteId);
  if (!site) return 1;
  return 1 - (1 - (site.productMult ?? 1)) * fi(state);
}

// Packaging cost per unit sold (barrels leave with the product; domestic bulk share needs none). Era $.
export function packagingPerUnit(state, pid, exportCargo = false) {
  if (!PRODUCTS[pid]?.barrels) return 0;
  const b = barrelDef();
  const perGal = b ? unitPrice(state, b) / GAL_PER_BBL : eraDollars(state, 2.5, 1859) / GAL_PER_BBL;
  return perGal * (exportCargo ? 1 : 1 - interp(BULK_SHARE, yf(state)));
}

function kerosQualityMult(ref) {
  const flashM = fireTestPriceMult(ref.kero?.flash ?? FIRE_TEST.default);
  const sk = clamp(ref.kero?.skunk ?? 0, 0, 1);
  return flashM * (1 - sk * (1 - SKUNK_PRICE_MULT));
}

// Gross price per unit at the site (before market depth and packaging).
export function siteGrossPrice(state, ref, pid) {
  let p = productPrice(state, pid) * siteProductMult(state, ref.siteId);
  if (pid === 'kerosene') p *= kerosQualityMult(ref) * squeezeProductMult(state, ref.siteId);
  else if (pid === 'gasoline' && ref.gaso) p *= octanePriceMult(ref.gaso.octane, yf(state));
  else if (pid === 'diesel') p *= dieselSulfurMult(state, ref);
  return p;
}

// Net price per unit for selling q units now (depth + packaging), and the gross.
export function sitePrice(state, ref, pid, q = 1) {
  const gross = siteGrossPrice(state, ref, pid);
  const D = productDepth(pid, yf(state));
  const q0 = soldToday(state, siteRegionKey(ref), pid);
  const mult = depthMult(D, q0, Math.max(0, q));
  const pack = packagingPerUnit(state, pid);
  return { gross, depth: mult, pack, net: gross * mult - pack };
}

function soldToday(state, key, pid) {
  const st = state.refining.soldToday;
  if (st.day !== state.day) return 0;
  return st.by[`${key}:${pid}`] ?? 0;
}
function addSold(state, key, pid, q) {
  const st = state.refining.soldToday;
  if (st.day !== state.day) { st.day = state.day; st.by = {}; }
  st.by[`${key}:${pid}`] = (st.by[`${key}:${pid}`] ?? 0) + q;
}

// Crude grades a refinery can buy on the spot market.
export function buyableGrades(state, ref) {
  const site = siteDef(ref.siteId);
  if (ref.siteId === 'field_side') {
    const g = gradeForField(ref.fieldId);
    const region = fieldDef(ref.fieldId)?.regionId;
    return g === 'penn' || region === 'pennsylvania' ? [g] : [g];
  }
  const list = site?.canBuyGrades ?? ['penn'];
  return list.filter((g) => g !== 'lima_sour' || yearOf(state) >= 1885);
}

// Posted price for a grade before market impact (era $/bbl at the wellhead).
export function gradeSpot(state, grade) {
  const spot = Math.max(0, spotPrice(state));
  const gm = GRADE_PRICE_MULT[grade] ?? 1;
  if (typeof gm === 'number') return spot * gm;
  return spot * (flagOn(state, gm.flag) ? gm.after : gm.before);
}

// Freight to bring one bbl of spot crude to the refinery (era $), incl. the squeeze.
export function crudeFreight(state, ref, own = false) {
  const site = siteDef(ref.siteId);
  const base = own && site?.ownCrudeFreight != null ? site.ownCrudeFreight : site?.crudeFreight ?? 0;
  return eraDollars(state, base * fi(state), 1865) + (own && ref.siteId === 'field_side' ? 0 : squeezeFreight(state, ref.siteId));
}

// Average delivered price per bbl for buying `bbl` today (buyers push the posted price up in thin markets).
export function crudeQuote(state, ref, bbl, grade = null) {
  const g = grade ?? buyableGrades(state, ref)[0] ?? 'penn';
  const posted = gradeSpot(state, g);
  const D = Math.max(50, marketDepth(state, ref.fieldId ?? 'oil_creek'));
  const b0 = state.refining?.boughtToday?.day === state.day ? state.refining.boughtToday.bbl : 0;
  const impact = 1 + (b0 + Math.max(0, bbl) / 2) / D;
  const freight = crudeFreight(state, ref);
  return { grade: g, posted, impact, freight, price: posted * impact + freight };
}

// ================================================================ daily tick
export function tickRefining(state) {
  if (!state.refineries || !state.refining || !state.productMarkets) initRefining(state);
  tickProductMarkets(state);
  fireRefiningNews(state);
  const y = yearOf(state);
  for (const ref of Object.values(state.refineries)) {
    normalizeRefinery(ref, y);
    ref.today = emptyToday(state.day, plist(ref));
    finishConstruction(state, ref);
    intakeRouted(state, ref);
    autoBuy(state, ref);
    runStills(state, ref);
    sellProducts(state, ref);
    lampRisk(state, ref);
    inspections(state, ref, y);
    refineryIncidents(state, ref);
    upkeep(state, ref);
    closeDay(state, ref);
  }
  tickShipments(state);
  tickStandardOil(state);
  tickShares(state);
}

function fireRefiningNews(state) {
  state.newsFired ??= {};
  state.news ??= [];
  for (const n of REFINING_NEWS) {
    if (n.day !== state.day || state.newsFired[n.id]) continue;
    state.newsFired[n.id] = state.day;
    if (n.effect?.type === 'flag') { state.flags ??= {}; state.flags[n.effect.flag] = n.effect.value; }
    const item = { id: n.id, day: state.day, text: n.headline, kind: n.kind, fieldId: null, regionId: null };
    state.news.push(item);
    notify(state, 'news', { ...item, pause: !!n.pause, level: n.pause ? 'major' : 'news' });
  }
}

function finishConstruction(state, ref) {
  if (!ref.pending.length) return;
  const done = ref.pending.filter((p) => p.readyDay <= state.day);
  if (!done.length) return;
  ref.pending = ref.pending.filter((p) => p.readyDay > state.day);
  for (const p of done) {
    ref.units.push(p.defId);
    log(state, `${ref.name}: ${unitDef(p.defId)?.name ?? p.defId} is fired up and ready.`, 'info', { refineryUid: ref.uid });
  }
  if (!ref.throughputSet) ref.throughputBbl = Math.round(refineryCapacity({ ...ref, condition: 100 }));
}

// Own crude routed from fields: pulled from field storage up to haulage capacity and tank room.
function intakeRouted(state, ref) {
  const routes = state.refining.routes;
  for (const [fid, uid] of Object.entries(routes)) {
    if (uid !== ref.uid) continue;
    const f = state.fields?.[fid];
    if (!f?.storage) continue;
    const room = Math.max(0, crudeCapacity(ref) - crudeTotal(ref));
    if (room <= 0.5) break;
    const local = ref.siteId === 'field_side' && ref.fieldId === fid;
    const cap = local ? Infinity : Math.max(0, transportCapacity(state, fid) - (f.shippedToday ?? 0));
    const amt = Math.min(f.storage.bbl, cap, room);
    if (!(amt > 0.01)) continue;
    const haul = local ? crudeFreight(state, ref, true) : transportCostPerBbl(state, fid) + crudeFreight(state, ref);
    f.storage.bbl = Math.max(0, f.storage.bbl - amt);
    if (f.storage.barrels > f.storage.bbl) f.storage.barrels = Math.floor(f.storage.bbl);
    if (!local) f.shippedToday = (f.shippedToday ?? 0) + amt;
    const spot = Math.max(0, spotPrice(state));
    const royalty = amt * spot * royaltyRate(state, fid);
    spend(state, royalty, 'royalties', false);
    spend(state, amt * haul, PL.opex, false);
    const grade = gradeForField(fid);
    ref.crude[grade] += amt;
    ref.crudeCost += royalty + amt * haul;
    ref.today.intake += amt;
    ref.today.freight += amt * haul;
  }
}

function autoBuy(state, ref) {
  const ab = ref.autoBuy;
  if (!ab?.on || !ref.units.length) return;
  // fillPct (set from the Refinery screen): keep the crude tanks that full; otherwise the old '3 days of runs'
  const room = Math.max(0, crudeCapacity(ref) - crudeTotal(ref));
  let want = Math.min(ab.fillPct != null ? Math.max(0, ab.fillPct * crudeCapacity(ref) - crudeTotal(ref)) : Math.max(0, ref.throughputBbl * 3 - crudeTotal(ref)), room);
  if (want < 1) return;
  let q = crudeQuote(state, ref, want, ab.grade);
  // big orders cost more per barrel (freight, local supply): with a fill target, buy the largest lot under the limit
  if (q.price > ab.maxPrice && ab.fillPct != null) for (let lot = want / 2; lot >= Math.max(1, ref.throughputBbl * 0.5); lot /= 2) { const ql = crudeQuote(state, ref, lot, ab.grade); if (ql.price <= ab.maxPrice) { q = ql; want = lot; break; } }
  if (q.price > ab.maxPrice) {
    if (ab.fillPct != null && !ab.priceBlocked) { ab.priceBlocked = true; notify(state, 'news', { refUid: ref.uid, kind: 'market', text: `${ref.name}: auto-buy paused — crude is ${fmt$(q.price)}/bbl delivered, above your ${fmt$(ab.maxPrice)} limit.`, level: 'warn' }); }
    return;
  }
  ab.priceBlocked = false;
  buyCrudeImpl(state, ref.uid, want, q.grade, true);
}

function runStills(state, ref) {
  if (state.day < ref.downUntil) return;
  const cap = refineryCapacity(ref);
  const have = crudeTotal(ref);
  let run = Math.min(Math.max(0, ref.throughputBbl), cap, have);
  if (run < 0.01) return;
  // product tank room (dumpable cuts don't need room if dumping is on)
  const shares = {};
  for (const g of Object.keys(ref.crude)) shares[g] = have > 0 ? ref.crude[g] / have : 0;
  let galPerBbl = 0;
  const PL_ = plist(ref);
  for (const [g, sh] of Object.entries(shares)) {
    if (!sh) continue;
    const yv = yieldsAt(state, ref, g, ref.fireTest, run).y;
    for (const p of PL_) {
      if ((ref.sell[p]?.mode === 'dump') && PRODUCTS[p].dumpable) continue;
      galPerBbl += sh * yv[p] * GAL_PER_BBL;
    }
  }
  const room = Math.max(0, productCapacity(ref) - storedGal(ref));
  if (galPerBbl > 0 && run * galPerBbl > room) run = room / galPerBbl;
  if (run < 0.01) {
    if (!ref.fullWarned) { ref.fullWarned = true; notify(state, 'storageFull', { refineryUid: ref.uid, text: `${ref.name}: product tanks are full — the stills are cold. Sell, export, or build tankage.`, level: 'warn' }); }
    return;
  }
  ref.fullWarned = false;

  // sour crude through the Frasch treater
  const sourRun = run * (shares.lima_sour ?? 0);
  const treated = Math.min(sourRun, treatCapacity(ref));
  const ownsFrasch = !!state.research?.done?.frasch_desulfurization;
  const tdef = unitDef('frasch_unit');
  const fTreated = Math.min(treated, Math.max(0, treatCapacity(ref) - hydroCap(ref))); // the rest goes through a hydrotreater (costed in processCosts)
  if (fTreated > 0) {
    const fee = !ownsFrasch && yearOf(state) < (tdef.patentUntil ?? 1905) ? eraDollars(state, tdef.licenseFeePerBbl, 1888) * fTreated : 0;
    const op = eraDollars(state, tdef.opexPerBbl + tdef.fuelPerBbl, 1888) * fTreated;
    spend(state, fee + op, PL.opex, false);
    ref.today.license += fee; ref.today.opex += op;
  }

  // take crude, average cost basis
  const avgCost = have > 0 ? ref.crudeCost / have : 0;
  for (const g of Object.keys(ref.crude)) ref.crude[g] = Math.max(0, ref.crude[g] - run * shares[g]);
  ref.crudeCost = Math.max(0, ref.crudeCost - avgCost * run);
  ref.today.crudeCost += avgCost * run;

  // products
  const made = zeroOf(PL_);
  let skunkKero = 0, gasOct = 0;
  const load = {};
  for (const [g, sh] of Object.entries(shares)) {
    if (!sh) continue;
    const ch = yieldsAt(state, ref, g, ref.fireTest, run);
    const yv = ch.y;
    const bbl = run * sh;
    for (const p of PL_) made[p] += bbl * yv[p] * GAL_PER_BBL * (p === 'wax' ? WAX_LB_PER_GAL : 1);
    gasOct += bbl * yv.gasoline * GAL_PER_BBL * ch.octane;
    for (const [id, b] of Object.entries(ch.load)) load[id] = (load[id] ?? 0) + b * sh;
    if (g === 'lima_sour') {
      const untreatedShare = bbl > 0 ? Math.max(0, bbl - treated) / bbl : 0;
      skunkKero += bbl * yv.kerosene * GAL_PER_BBL * untreatedShare;
    }
  }
  // blend kerosene quality into the tank
  const k0 = ref.products.kerosene, k1 = made.kerosene;
  if (k1 > 0) {
    const tot = k0 + k1;
    ref.kero.flash = (k0 * ref.kero.flash + k1 * ref.fireTest) / tot;
    ref.kero.skunk = (k0 * ref.kero.skunk + skunkKero) / tot;
  }
  if (ref.gaso && made.gasoline > 0) {
    const g0 = ref.products.gasoline, g1 = made.gasoline;
    ref.gaso.octane = r2((g0 * ref.gaso.octane + gasOct) / (g0 + g1));
  }
  for (const p of PL_) { ref.products[p] += made[p]; ref.today.made[p] = made[p]; }
  if (Object.keys(load).length) ref.today.load = load;

  // opex & fuel (unit base-year dollars), weighted by capacity share
  const st = stillUnits(ref);
  const totCap = st.reduce((a, u) => a + u.capacityBbl, 0) || 1;
  let opex = 0, coal = 0, residNeed = 0;
  for (const u of st) {
    const w = u.capacityBbl / totCap;
    opex += w * eraDollars(state, u.opexPerBbl, u.baseYear ?? u.year);
    coal += w * eraDollars(state, u.fuelPerBbl, u.baseYear ?? u.year);
    residNeed += w * (u.residGalPerBbl ?? 1);
  }
  opex *= run * costMult(state);
  let fuelCost = coal * run * costMult(state);
  if (ref.burnResiduum && residNeed > 0) {
    const need = residNeed * run;
    const use = Math.min(need, ref.products.residuum);
    ref.products.residuum -= use;
    fuelCost *= 1 - use / need;
  }
  if (yf(state) > 1900 || Object.keys(load).length) { // conversion / treating / emissions (none of it exists in M1)
    const pc = processCosts(state, ref, load, run);
    opex += pc.opex; fuelCost += pc.fuel;
  }
  spend(state, opex + fuelCost, PL.opex, false);
  ref.today.opex += opex; ref.today.fuel += fuelCost;
  ref.today.run = run;
  ref.stats.bblRun += run;
}

// Ship capacity for products leaving the site today (gallons).
export function productShipCap(state, ref) {
  if (ref.siteId !== 'field_side') return RAIL_SHIP_GAL;
  const f = state.fields?.[ref.fieldId];
  const linked = f && (['rail', 'gathering_pipe', 'trunk_pipeline'].includes(f.transportId) || (f.connected ?? []).some((t) => t === 'rail'));
  const y = yf(state);
  if (y > 1900) return interp(linked ? FIELD_SHIP_LINKED_GAL : FIELD_SHIP_GAL, y); // tank trucks, product pipelines
  return linked ? 50000 : FIELD_SIDE_WAGON_GAL;
}

function sellProducts(state, ref) {
  let shipLeft = productShipCap(state, ref) - (ref.shippedGalToday ?? 0);
  const fines = dumpingFine(state);
  for (const pid of plist(ref)) {
    const mode = ref.sell[pid]?.mode ?? 'hold';
    const stock = ref.products[pid];
    if (stock <= 1e-6) continue;
    if (mode === 'dump' && PRODUCTS[pid].dumpable) {
      ref.products[pid] = 0;
      ref.today.dumped += stock;
      ref.stats.dumpedGal += stock;
      if (fines > 0) { spend(state, stock * fines, 'other', false); }
      continue;
    }
    if (mode === 'export') { maybeExport(state, ref); continue; }
    if (mode !== 'auto') continue;
    // auto + a dumpable cut nobody will buy + tanks nearly full: run the surplus into the creek (as refiners did)
    if (PRODUCTS[pid].dumpable && storedGal(ref) > AUTO_DUMP_FILL * productCapacity(ref)) {
      const keep = (ref.today.made[pid] ?? 0) * 2 + Math.min(stock, profitableQty(state, ref, pid));
      const surplus = Math.max(0, stock - keep);
      if (surplus > 1) {
        ref.products[pid] -= surplus;
        ref.today.dumped += surplus; ref.stats.dumpedGal += surplus;
        if (fines > 0) spend(state, surplus * fines, 'other', false);
        if (!ref.dumpWarned) { ref.dumpWarned = true; notify(state, 'news', { kind: 'market', refineryUid: ref.uid, text: `${ref.name}: tanks nearly full and nobody will pay for the ${PRODUCTS[pid].name.toLowerCase()} — the surplus is going into the creek.${fines > 0 ? ` Fined ${fmt$(fines)}/gal under the Refuse Act.` : ''}`, level: 'warn' }); }
      }
    }
    if (pid === 'kerosene' && ref.kero.skunk > 0.5 && !ref.skunkWarned) {
      ref.skunkWarned = true;
      notify(state, 'news', { kind: 'market', refineryUid: ref.uid, text: `${ref.name}: your Lima kerosene stinks of sulfur — buyers pay ${Math.round(SKUNK_PRICE_MULT * 100)}% for "skunk oil". A Frasch unit (1888) fixes it.`, level: 'warn' });
    }
    let want = stock * clamp(ref.sell[pid]?.pct ?? 100, 0, 100) / 100;
    const galEq = pid === 'wax' ? want / WAX_LB_PER_GAL : want;
    if (pid !== 'residuum' && galEq > shipLeft) want = Math.max(0, shipLeft) * (pid === 'wax' ? WAX_LB_PER_GAL : 1);
    // sell only down to the point where the next gallon no longer pays for its barrel (thin markets)
    want = Math.min(want, profitableQty(state, ref, pid));
    if (want < 1) continue;
    const pr = sitePrice(state, ref, pid, want);
    if (pr.net <= 0) continue; // not worth the barrel — hold (or dump)
    sellNow(state, ref, pid, want, pr);
    if (pid !== 'residuum') shipLeft -= pid === 'wax' ? want / WAX_LB_PER_GAL : want;
  }
}

// Units that can be sold today before the marginal price (gross / (1 + Q/D)) falls to the packaging cost.
export function profitableQty(state, ref, pid) {
  const gross = siteGrossPrice(state, ref, pid);
  const pack = packagingPerUnit(state, pid);
  const D = productDepth(pid, yf(state));
  const q0 = soldToday(state, siteRegionKey(ref), pid);
  if (gross <= pack) return 0;
  if (!(pack > 0) || !(D > 0)) return Infinity;
  return Math.max(0, D * (gross / pack - 1) - q0);
}

function sellNow(state, ref, pid, qty, pr = null) {
  pr ??= sitePrice(state, ref, pid, qty);
  const gross = qty * pr.gross * pr.depth;
  const pack = qty * pr.pack;
  ref.products[pid] = Math.max(0, ref.products[pid] - qty);
  addSold(state, siteRegionKey(ref), pid, qty);
  earn(state, gross, PL.sales);
  spend(state, pack, PL.opex, false);
  ref.today.sold[pid] += qty;
  ref.today.revenue += gross;
  ref.today.packaging += pack;
  ref.stats.revenue += gross;
  if (pid !== 'residuum' && pid !== 'wax') ref.shippedGalToday = (ref.shippedGalToday ?? 0) + qty;
  return gross - pack;
}

export function dumpingFine(state) {
  if (!flagOn(state, 'refuseAct')) return 0;
  return interp(DUMPING_FINES, yf(state));
}

// ---------------------------------------------------------------- exports (NYC → Europe)
function maybeExport(state, ref) {
  const site = siteDef(ref.siteId);
  const gal = ref.products.kerosene;
  if (!site?.export || !flagOn(state, 'keroseneExport') || gal < EXPORT_MIN_LOT_GAL) return;
  const y = yf(state);
  const pack = packagingPerUnit(state, 'kerosene', true) * gal;
  const freight = oceanFreight(y) * gal;
  spend(state, pack + freight, PL.opex, false);
  ref.today.packaging += pack; ref.today.freight += freight;
  state.refining.shipments.push({ id: nextId(state, 'xs'), refineryUid: ref.uid, name: ref.name, departDay: state.day, arriveDay: state.day + voyageDays(y), gal, flash: ref.kero.flash, skunk: ref.kero.skunk });
  ref.products.kerosene = 0;
  ref.stats.exportedGal += gal;
  ref.today.sold.kerosene += gal;
  log(state, `${ref.name}: ${Math.round(gal).toLocaleString('en-US')} gal of kerosene cleared for Liverpool.`, 'info', { refineryUid: ref.uid });
}

function tickShipments(state) {
  const sh = state.refining.shipments;
  if (!sh.length) return;
  const keep = [];
  for (const s of sh) {
    if (s.arriveDay > state.day) { keep.push(s); continue; }
    if (s.flash < EXPORT_MIN_FIRE_TEST) {
      state.company.reputation = Math.max(0, state.company.reputation - 2);
      notify(state, 'news', { kind: 'market', refineryUid: s.refineryUid, text: `Cargo condemned at Liverpool: ${Math.round(s.gal).toLocaleString('en-US')} gal of ${s.name} kerosene flashed below the Petroleum Act's ${EXPORT_MIN_FIRE_TEST}°F.`, level: 'warn' });
      continue;
    }
    if (chance(state, EXPORT_LOSS_CHANCE)) {
      notify(state, 'news', { kind: 'market', refineryUid: s.refineryUid, text: `Lost at sea: the bark carrying ${Math.round(s.gal).toLocaleString('en-US')} gal of ${s.name} kerosene burned off the Banks.`, level: 'warn' });
      continue;
    }
    const y = yf(state);
    const D = productDepth('kerosene', y) * EXPORT_DEPTH_MULT;
    const q0 = state.refining.soldToday.day === state.day ? state.refining.soldToday.by['europe:kerosene'] ?? 0 : 0;
    const price = productPrice(state, 'kerosene') * exportPremium(y) * fireTestPriceMult(s.flash) * (1 - s.skunk * (1 - SKUNK_PRICE_MULT)) * depthMult(D, q0, s.gal);
    addSold(state, 'europe', 'kerosene', s.gal);
    const gross = price * s.gal;
    earn(state, gross, PL.sales);
    const ref = state.refineries[s.refineryUid];
    if (ref) ref.stats.revenue += gross;
    log(state, `Export cargo from ${s.name} sold in Europe: ${Math.round(s.gal).toLocaleString('en-US')} gal at ${fmt$(price)}/gal = ${fmt$(gross)}.`, 'info', { refineryUid: s.refineryUid });
  }
  state.refining.shipments = keep;
}

// ---------------------------------------------------------------- lamp explosions & fire-test laws
function lampRisk(state, ref) {
  const gal = ref.today.sold.kerosene;
  if (!(gal > 0)) return;
  const exportOnly = ref.sell.kerosene?.mode === 'export';
  if (exportOnly) return; // abroad, the cargo test catches it (tickShipments)
  const p = Math.min(0.9, gal * lampRatePerGal(ref.kero.flash) * interp(LAMP_ERA_MULT, yf(state)));
  if (!chance(state, p)) return;
  const fatal = chance(state, LAMP_SUIT.fatalChance);
  const dmg = eraDollars(state, range(state, LAMP_SUIT.damages[0], LAMP_SUIT.damages[1]) * (fatal ? 2 : 1), LAMP_SUIT.baseYear);
  spend(state, dmg, 'other', false);
  state.company.reputation = Math.max(0, state.company.reputation - (fatal ? LAMP_SUIT.repLossFatal : LAMP_SUIT.repLoss));
  state.refining.lampSuits++;
  const town = pick(state, ['Erie', 'Buffalo', 'Rochester', 'Albany', 'Hartford', 'Baltimore', 'Cincinnati', 'Chicago', 'Toledo', 'Wheeling']);
  notify(state, 'news', { kind: 'disaster', refineryUid: ref.uid, pause: fatal, text: `Lamp explodes in a ${town} parlor${fatal ? ' — a child is dead' : ''}; the oil is traced to ${ref.name} (fire test ${Math.round(ref.kero.flash)}°F). Damages ${fmt$(dmg)}.`, level: 'warn' });
}

function inspections(state, ref, year) {
  const min = flagOn(state, 'fireTestLaw') ? legalFireTest(year) : null;
  if (!min || ref.products.kerosene < 1 || ref.kero.flash >= min - 0.5) return;
  if (!chance(state, INSPECTION.chancePerDay)) return;
  const fine = eraDollars(state, INSPECTION.fine, INSPECTION.baseYear);
  const lost = ref.products.kerosene * INSPECTION.condemnPct;
  ref.products.kerosene -= lost;
  ref.downUntil = Math.max(ref.downUntil, state.day + (INSPECTION.shutDays ?? 0));
  spend(state, fine, 'other', false);
  state.company.reputation = Math.max(0, state.company.reputation - 2);
  state.refining.inspections++;
  notify(state, 'news', { kind: 'politics', refineryUid: ref.uid, pause: true, text: `State oil inspector at ${ref.name}: your kerosene flashes at ${Math.round(ref.kero.flash)}°F, under the ${min}°F law. Fined ${fmt$(fine)}; ${Math.round(lost).toLocaleString('en-US')} gal condemned and the works posted shut for ${INSPECTION.shutDays} days.`, level: 'warn' });
}

// ---------------------------------------------------------------- still fires & explosions (DESIGN §15 pattern)
function utilization(ref) {
  const cap = refineryCapacity(ref);
  return cap > 0 ? clamp(ref.today.run / cap, 0, 1) : 0;
}
const lightEnds = (ref) => 1 + 0.6 * Math.max(0, (FIRE_TEST.default - ref.fireTest) / 40);
export const REFINERY_ROWS = REFINERY_INCIDENTS.map((row) => ({
  ...row,
  exposure: (s, { refinery: ref }) => {
    if (!ref) return 0;
    const u = utilization(ref);
    const tanks = ref.units.map(unitDef).filter((d) => d?.kind === 'tank').length;
    if (row.id === 'still_fire') {
      const stills = ref.units.map(unitDef).filter((d) => d && d.kind !== 'tank').reduce((a, d) => a + (d.fireRisk ?? 0), 0);
      return (stills * u + 0.1 * tanks * (storedGal(ref) > 0 ? 1 : 0)) * lightEnds(ref) * (ref.condition < 50 ? 1.5 : 1);
    }
    const ex = ref.units.map(unitDef).filter((d) => d && d.kind !== 'tank').reduce((a, d) => a + (d.explosionRisk ?? 0), 0);
    return ex * u * lightEnds(ref) * (ref.condition < 40 ? 2 : 1);
  },
}));

function stillSafetyMult(state) {
  const has = !!state.research?.upgrades?.still_safety || yf(state) >= 1875;
  return has ? { still_fire: 0.65, refinery_explosion: 0.7 } : { still_fire: 1, refinery_explosion: 1 };
}

export function refineryIncidentChance(state, ref, row) {
  const ctx = { refinery: ref, crew: [], rig: null, field: { id: ref.uid } };
  let p = incidentChance(state, row, ctx);
  if (!(p > 0)) return 0;
  p *= 1 - budgetMitigation(state, ref.safetyBudget ?? 0);
  p *= stillSafetyMult(state)[row.id] ?? 1;
  p *= interp(PROCESS_SAFETY_MULT, yf(state));
  return p;
}

function refineryIncidents(state, ref) {
  if (!state.safety) return;
  ref.daysWithoutIncident++;
  if (ref.safetyBudget > 0 && ref.units.length) spend(state, ref.safetyBudget, 'other', false);
  for (const row of REFINERY_ROWS) {
    const p = refineryIncidentChance(state, ref, row);
    if (p <= 0) continue;
    const key = `${ref.uid}:${row.id}`;
    const hasWarn = Object.values(state.safety.warnings ?? {}).some((w) => w.key === key);
    if (!hasWarn && chance(state, Math.min(0.3, p * 4))) issueWarning(state, ref, row, key);
    if (chance(state, p)) refineryAccident(state, ref, row, key);
  }
}

function issueWarning(state, ref, row, key) {
  const id = nextId(state, 'sw');
  const cost = Math.round(eraDollars(state, row.warnCost ?? 10, 1860) * 100) / 100;
  const w = { id, key, type: row.id, rigUid: null, wellId: null, fieldId: ref.fieldId ?? null, refineryUid: ref.uid, day: state.day, expires: state.day + 5, cost, responded: false, text: `${ref.name}: ${row.warning}.` };
  (state.safety.warnings ??= {})[id] = w;
  notify(state, 'warning', { warningId: id, incidentType: row.id, refineryUid: ref.uid, fieldId: w.fieldId, text: `${w.text} Respond for ${fmt$(cost)}?`, level: 'warn' });
}

function refineryAccident(state, ref, row, key) {
  const head = pick(state, row.templates ?? [`${row.name} at {refinery}.`]).replace('{refinery}', ref.name);
  if (!state.safety.seen?.[row.id]) {
    (state.safety.seen ??= {})[row.id] = state.day;
    notify(state, 'warning', { nearMiss: true, incidentType: row.id, refineryUid: ref.uid, text: `Near miss — ${row.name}: the stillmen beat it out in time at ${ref.name}. Watch for: “${row.warning}”.`, level: 'warn' });
    return;
  }
  const preventable = (state.safety.ignored?.[key] ?? -1) >= state.day;
  const hands = ref.units.map(unitDef).reduce((a, u) => a + (u?.hands ?? 0), 0);
  const n = Math.min(Math.max(1, hands), int(state, row.victims?.[0] ?? 1, row.victims?.[1] ?? 1));
  const fates = [];
  let anyDead = false;
  const y = yearOf(state);
  for (let i = 0; i < n; i++) {
    const c = makeCrew(state, 'laborer');
    c.role = 'stillman';
    if (chance(state, row.fatal)) {
      anyDead = true;
      c.status = 'dead';
      state.company.stats.deaths = (state.company.stats.deaths ?? 0) + 1;
      state.company.reputation = Math.max(0, state.company.reputation - (preventable ? 20 : 10));
      (state.memorial ??= []).push({ name: c.name, role: 'stillman', born: y - c.age, died: y, day: state.day, cause: row.name, refineryUid: ref.uid });
      handleDeath(state, c, row.name.toLowerCase());
      fates.push(`${c.name} (stillman) was killed`);
    } else {
      state.company.stats.injuries = (state.company.stats.injuries ?? 0) + 1;
      spend(state, eraDollars(state, 20, 1870), 'other', false);
      fates.push(`${c.name} (stillman) was burned`);
    }
  }
  state.company.accidentFreeSince = state.day;
  state.safety.daysWithoutIncident = 0;
  ref.daysWithoutIncident = 0;
  // damage
  let eff;
  if (row.id === 'still_fire') {
    const d = range(state, 15, 40);
    ref.condition = Math.max(0, ref.condition - d);
    ref.downUntil = state.day + int(state, 3, 10);
    const burnK = range(state, 0.1, 0.3);
    ref.products.kerosene *= 1 - burnK;
    ref.products.naphtha *= 1 - range(state, 0.3, 0.8);
    for (const g of Object.keys(ref.crude)) ref.crude[g] *= 0.9;
    eff = `Works damaged ${Math.round(d)}%, down ${ref.downUntil - state.day} days; ${Math.round(burnK * 100)}% of the kerosene burned.`;
  } else {
    const d = range(state, 40, 80);
    ref.condition = Math.max(0, ref.condition - d);
    ref.downUntil = state.day + int(state, 15, 40);
    let lostUnit = '';
    const modern = yf(state) > 1900;
    const stills = ref.units.map((id, i) => ({ id, i, u: unitDef(id) })).filter((x) => (modern ? x.u && x.u.kind !== 'tank' : x.u?.kind === 'still'));
    if (stills.length && (stills.length > 1 || ref.condition < 30 || chance(state, 0.3))) {
      // pioneer works lose their biggest still; a modern plant loses its most explosive unit (cracker, alky, hydrocracker)
      const big = stills.reduce((a, b) => (modern ? (b.u.explosionRisk ?? 0) > (a.u.explosionRisk ?? 0) : b.u.capacityBbl > a.u.capacityBbl) ? b : a);
      ref.units.splice(big.i, 1);
      lostUnit = ` The ${big.u.name} is wrecked.`;
    }
    for (const p of plist(ref)) ref.products[p] *= range(state, 0.3, 0.8);
    eff = `Works damaged ${Math.round(d)}%, down ${ref.downUntil - state.day} days.${lostUnit}`;
  }
  ref.fire = { day: state.day, untilDay: state.day + 4, type: row.id };
  notify(state, 'accident', { incidentType: row.id, refineryUid: ref.uid, fieldId: ref.fieldId ?? null, wellId: null, rigUid: null, pause: true, preventable, fatal: anyDead, text: `${head} ${fates.join('; ')}. ${eff}${preventable ? ' The warning signs were ignored.' : ''}`, level: 'warn' });
}

// ---------------------------------------------------------------- upkeep: auto-repairs
// Fixed cost per day: stillmen's wages + overhead on the plant (paid even when the stills are cold).
export function fixedCostPerDay(state, ref) {
  if (!ref.units.length) return 0;
  const hands = ref.units.map(unitDef).reduce((a, u) => a + (u?.hands ?? 0), 0);
  return (hands * marketWage('laborer', yearOf(state), 50) * STILLMAN_WAGE_MULT + replacementValue(state, ref) * OVERHEAD_PER_DAY) * costMult(state);
}

function upkeep(state, ref) {
  const fixed = fixedCostPerDay(state, ref);
  if (fixed > 0) { spend(state, fixed, PL.opex, false); ref.today.opex += fixed; ref.today.fixed = fixed; }
  if (ref.condition >= 100 || state.day < ref.downUntil || !ref.units.length) return;
  const value = replacementValue(state, ref);
  const pts = Math.min(1.5, 100 - ref.condition);
  const cost = value * 0.004 * pts;
  spend(state, cost, PL.opex, false);
  ref.condition = Math.min(100, ref.condition + pts);
  ref.today.opex += cost;
}

function closeDay(state, ref) {
  const t = ref.today;
  t.margin = t.revenue - t.crudeCost - t.opex - t.fuel - t.packaging - t.license;
  ref.stats.costs += t.opex + t.fuel + t.packaging + t.license + t.freight;
  ref.crudeBbl = crudeTotal(ref);
  ref.shippedGalToday = 0;
  if (!ref.history.length || state.day - ref.history[ref.history.length - 1][0] >= 7) {
    const m = marginView(state, ref.uid);
    ref.history.push([state.day, r2(m?.netPerBbl ?? 0)]);
    if (ref.history.length > 520) ref.history.splice(0, ref.history.length - 520);
  }
}

// ================================================================ Standard Oil
function waveMult(year) {
  for (const [a, b, m] of STANDARD_OIL.waves) if (year >= a && year < b) return m;
  return 1;
}

function tickStandardOil(state) {
  const y = yearOf(state);
  const so = STANDARD_OIL;
  const st = state.refining.standard;
  // expire unanswered offers (counted as a refusal)
  for (const o of Object.values(st.offers)) {
    if (o.status === 'pending' && state.day >= o.expiresDay) respondStandardOfferImpl(state, o.id, 'refuse', true);
  }
  if (y < so.fromYear || y > so.toYear || state.day < so.firstOfferDay) return;
  const first = !st.firstDone;
  if (!first && !isFirstOfMonth(state.day)) return;
  for (const ref of Object.values(state.refineries)) {
    if (!ref.units.length || refineryCapacity(ref) < so.minCapacityBbl) continue;
    if (Object.values(st.offers).some((o) => o.status === 'pending' && o.refineryUid === ref.uid)) continue;
    if (state.day - (st.lastOfferDay[ref.uid] ?? -1e9) < so.cooldownDays) continue;
    const site = siteDef(ref.siteId);
    const p = so.monthlyChance * (site?.standardHome ?? 1) * waveMult(y) * (refineryCapacity(ref) >= 400 ? 1.5 : 1);
    if (!first && !chance(state, p)) continue;
    makeOffer(state, ref);
    st.firstDone = true;
    break; // one visitor at a time
  }
}

export function replacementValue(state, ref) {
  let v = 0;
  for (const id of ref.units) v += unitPriceOf(state, id, ref.siteId);
  return v * (0.5 + 0.5 * ref.condition / 100);
}

function inventoryValue(state, ref) {
  let v = crudeTotal(ref) * Math.max(0, spotPrice(state));
  for (const p of plist(ref)) v += ref.products[p] * Math.max(0, siteGrossPrice(state, ref, p) - packagingPerUnit(state, p));
  return v;
}

function makeOffer(state, ref) {
  const so = STANDARD_OIL;
  const value = replacementValue(state, ref) + inventoryValue(state, ref) * 0.7;
  const sq = squeezeState(state);
  const cash = Math.round(value * range(state, so.cashPct[0], so.cashPct[1]) * (sq.active ? 0.85 : 1));
  const stock = Math.round(value * range(state, so.stockPct[0], so.stockPct[1]));
  const id = nextId(state, 'so');
  const offer = { id, refineryUid: ref.uid, name: ref.name, day: state.day, expiresDay: state.day + 30, cash, stock, status: 'pending', value: Math.round(value) };
  state.refining.standard.offers[id] = offer;
  state.refining.standard.lastOfferDay[ref.uid] = state.day;
  const text = `A quiet gentleman from the Standard Oil Company calls at ${ref.name}. He offers ${fmt$(cash)} cash — or ${fmt$(stock)} in Standard stock — for the works. ` +
    `"You may as well sell; you cannot compete." Refuse, and the railroads will remember.`;
  queueDecision(state, {
    kind: 'standardOffer', id, offerId: id, refineryUid: ref.uid, day: state.day, title: `Standard Oil wants ${ref.name}`, text,
    options: [
      { choice: 'cash', label: `Sell for ${fmt$(cash)} cash` },
      { choice: 'stock', label: `Take ${fmt$(stock)} in Standard stock` },
      { choice: 'refuse', label: 'Refuse — fight them' },
    ],
  });
  notify(state, 'news', { kind: 'market', refineryUid: ref.uid, pause: true, text: `Standard Oil makes an offer for ${ref.name}: ${fmt$(cash)} cash or ${fmt$(stock)} in stock.`, level: 'major' });
}

export function respondStandardOfferImpl(state, offerId, choice, auto = false) {
  initRefining(state);
  const st = state.refining.standard;
  const o = st.offers[offerId];
  if (!o || o.status !== 'pending') return { ok: false, msg: 'No such offer is open.' };
  if (!['cash', 'stock', 'refuse'].includes(choice)) return { ok: false, msg: 'Choose cash, stock or refuse.' };
  popDecision(state, offerId);
  o.status = choice;
  o.resolvedDay = state.day;
  const ref = state.refineries[o.refineryUid];
  if (choice === 'refuse' || !ref) {
    const so = STANDARD_OIL;
    const sq = state.refining.squeeze;
    sq.refusals = (sq.refusals ?? 0) + 1;
    sq.level = Math.min(so.maxLevel, (squeezeState(state).active ? sq.level : 0) + 1);
    sq.untilDay = Math.max(sq.untilDay ?? 0, state.day + Math.round(365 * (so.squeezeYears + so.squeezeYearsPerRefusal * (sq.refusals - 1))));
    const msg = `${auto ? 'You let the offer lapse. ' : ''}Standard's man tips his hat. Within the week the railroads raise your rates — the squeeze is on (level ${sq.level}) until ${dayToDate(sq.untilDay).year}. Pipelines and Tidewater soften it.`;
    notify(state, 'news', { kind: 'market', refineryUid: o.refineryUid, text: msg, level: 'warn' });
    return { ok: true, msg };
  }
  const name = ref.name;
  removeRefinery(state, ref.uid);
  st.sold = (st.sold ?? 0) + 1;
  if (choice === 'cash') {
    state.company.cash += o.cash;
    log(state, `Sold ${name} to Standard Oil for ${fmt$(o.cash)}.`, 'major');
    return { ok: true, msg: `Sold ${name} to Standard Oil for ${fmt$(o.cash)} cash.` };
  }
  const sh = (state.refining.shares ??= { value: 0, startDay: state.day, dividendsPaid: 0 });
  sh.value += o.stock;
  log(state, `Traded ${name} for ${fmt$(o.stock)} of Standard Oil stock.`, 'major');
  return { ok: true, msg: `${name} is Standard's now. You hold ${fmt$(sh.value)} of Standard stock (≈${Math.round(STANDARD_OIL.stockDividendPerYear * 100)}%/yr dividends).` };
}

function tickShares(state) {
  const sh = state.refining.shares;
  if (!sh?.value || !isFirstOfMonth(state.day)) return;
  const div = sh.value * STANDARD_OIL.stockDividendPerYear / 12;
  earn(state, div, PL.dividends);
  sh.dividendsPaid = (sh.dividendsPaid ?? 0) + div;
}

function removeRefinery(state, uid) {
  delete state.refineries[uid];
  for (const [fid, r] of Object.entries(state.refining.routes)) if (r === uid) delete state.refining.routes[fid];
}

// ================================================================ actions (all → { ok, msg })
const no = (msg) => ({ ok: false, msg });
const ok = (msg, extra = {}) => ({ ok: true, msg, ...extra });

export function buildRefineryImpl(state, siteId, opts = {}) {
  initRefining(state);
  const site = siteDef(siteId);
  if (!site || site.m > 1) return no('Unknown refinery site.');
  const y = yearOf(state);
  if (y < site.fromYear) return no(`Nobody refines at ${site.name} before ${site.fromYear}.`);
  const unitId = opts.unitId ?? 'cheese_box_still';
  const u = unitDef(unitId);
  if (!u || u.kind !== 'still' || u.prereqUnit) return no('Start a refinery with a still (cheese box, still house or works).');
  if (!unitAvailable(state, unitId)) return no(`${u.name} is not available until ${u.year}.`);
  let fieldId = null;
  if (site.needsField) {
    fieldId = opts.fieldId ?? Object.keys(state.fields ?? {}).find((f) => state.fields[f].plots?.some((p) => p.owned));
    if (!fieldId || !state.fields?.[fieldId]) return no('Choose a field where you hold a lease.');
    if (!state.fields[fieldId].plots?.some((p) => p.owned)) return no(`You need a lease at ${fieldDef(fieldId)?.name ?? fieldId} to build there.`);
  }
  const dupe = Object.values(state.refineries).find((r) => r.siteId === siteId && (!site.needsField || r.fieldId === fieldId));
  if (dupe) return no(`You already have ${dupe.name} there — add units to it instead.`);
  const price = unitPriceOf(state, unitId, siteId);
  if (!spend(state, price, 'capex')) return no(`${u.name} at ${site.name} costs ${fmt$(price)}.`);
  const uid = nextId(state, 'rf');
  const place = site.needsField ? fieldDef(fieldId)?.name?.split(',')[0] ?? fieldId : site.name;
  const ref = normalizeRefinery({
    uid, name: opts.name || `${place} Works`, siteId, fieldId, defId: unitId, units: [], pending: [{ defId: unitId, readyDay: state.day + (u.buildDays ?? 7) }],
    throughputBbl: u.capacityBbl, fireTest: FIRE_TEST.default, built: state.day,
  }, y);
  state.refineries[uid] = ref;
  if (site.needsField && opts.route !== false && !state.refining.routes[fieldId]) state.refining.routes[fieldId] = uid;
  return ok(`Building ${ref.name}: ${u.name} (${fmt$(price)}), ready in ${u.buildDays ?? 7} days.`, { uid });
}

export function addUnitImpl(state, refUid, unitId) {
  initRefining(state);
  const ref = state.refineries[refUid];
  const u = unitDef(unitId);
  if (!ref) return no('No such refinery.');
  if (!u || (u.m ?? 1) > 1 && !state.research?.unlockedDefs?.[unitId]) return no('Unknown unit.');
  if (!unitAvailable(state, unitId)) return no(`${u.name} is not available until ${u.year}${u.tech ? ' (or research it)' : ''}.`);
  const all = [...ref.units, ...ref.pending.map((p) => p.defId)];
  if (u.prereqUnit && !u.prereqUnit.some((p) => ref.units.includes(p))) return no(`${u.name} needs a ${u.prereqUnit.map((p) => unitDef(p)?.name).join(' or ')} running at the site first.`);
  if (all.filter((x) => x === unitId).length >= (u.maxPerSite ?? 99)) return no(`No room for another ${u.name} at ${ref.name}.`);
  const price = unitPriceOf(state, unitId, ref.siteId);
  if (!spend(state, price, 'capex')) return no(`${u.name} costs ${fmt$(price)}.`);
  ref.pending.push({ defId: unitId, readyDay: state.day + (u.buildDays ?? 7) });
  return ok(`${u.name} ordered for ${ref.name} (${fmt$(price)}), ready in ${u.buildDays ?? 7} days.`);
}

export function setThroughputImpl(state, refUid, bbl) {
  const ref = state.refineries?.[refUid];
  if (!ref) return no('No such refinery.');
  const cap = refineryCapacity({ ...ref, condition: 100 });
  ref.throughputBbl = clamp(Math.round(Number(bbl) || 0), 0, Math.max(cap, 0));
  ref.throughputSet = true;
  return ok(`${ref.name} will run ${ref.throughputBbl} bbl/day.`);
}

export function setFireTestImpl(state, refUid, f) {
  const ref = state.refineries?.[refUid];
  if (!ref) return no('No such refinery.');
  ref.fireTest = clamp(Math.round((Number(f) || FIRE_TEST.default) / FIRE_TEST.step) * FIRE_TEST.step, FIRE_TEST.min, FIRE_TEST.max);
  const min = flagOn(state, 'fireTestLaw') ? legalFireTest(yearOf(state)) : null;
  return ok(`${ref.name}: kerosene cut to a ${ref.fireTest}°F fire test.${min && ref.fireTest < min ? ` That is under the ${min}°F law — inspectors fine and condemn.` : ''}`);
}

export function buyCrudeImpl(state, refUid, bbl, grade = null, auto = false) {
  initRefining(state);
  const ref = state.refineries[refUid];
  if (!ref) return no('No such refinery.');
  if (!ref.units.length) return no(ref.pending.length ? `${ref.name} is still being built.` : 'This refinery has no stills.');
  const g = grade ?? buyableGrades(state, ref)[0];
  if (!buyableGrades(state, ref).includes(g)) return no(`${CRUDE_GRADES[g]?.name ?? g} is not sold at ${ref.name}.`);
  const room = Math.max(0, crudeCapacity(ref) - crudeTotal(ref));
  let q = Math.min(Math.max(0, Math.floor(Number(bbl) || 0)), Math.floor(room));
  if (q <= 0) return no(room < 1 ? `${ref.name}'s crude tanks are full.` : 'Enter an amount.');
  let quote = crudeQuote(state, ref, q, g);
  const afford = Math.floor(Math.max(0, state.company.cash) / Math.max(1e-6, quote.price));
  if (afford < q) { q = afford; if (q <= 0) return no(`Crude delivered costs ${fmt$(quote.price)}/bbl; not enough cash.`); quote = crudeQuote(state, ref, q, g); }
  const total = quote.price * q;
  spend(state, quote.posted * quote.impact * q, PL.crude, false);
  spend(state, quote.freight * q, PL.opex, false);
  const bt = state.refining.boughtToday;
  if (bt.day !== state.day) { bt.day = state.day; bt.bbl = 0; }
  bt.bbl += q;
  ref.crude[g] += q;
  ref.crudeCost += total;
  ref.crudeBbl = crudeTotal(ref);
  if (ref.today?.day === state.day) { ref.today.bought += q; ref.today.freight += quote.freight * q; }
  ref.stats.crudeBought += q;
  state.crudePurchases.push({ day: state.day, bbl: q, price: r2(quote.price), refineryUid: ref.uid, grade: g });
  if (state.crudePurchases.length > 300) state.crudePurchases.splice(0, state.crudePurchases.length - 250);
  return ok(`${auto ? 'Auto-bought' : 'Bought'} ${q.toLocaleString('en-US')} bbl of ${CRUDE_GRADES[g].name} for ${ref.name} at ${fmt$(quote.price)}/bbl delivered (${fmt$(total)}).`);
}

export function routeCrudeImpl(state, fieldId, refUid) {
  initRefining(state);
  if (!state.fields?.[fieldId]) return no('No such field.');
  if (refUid == null) {
    delete state.refining.routes[fieldId];
    return ok(`${fieldDef(fieldId)?.name ?? fieldId} crude goes back to market.`);
  }
  const ref = state.refineries[refUid];
  if (!ref) return no('No such refinery.');
  const local = ref.siteId === 'field_side' && ref.fieldId === fieldId;
  if (!local && transportCapacity(state, fieldId) <= 0) return no('That field has no transport to reach the refinery.');
  state.refining.routes[fieldId] = refUid;
  return ok(`${fieldDef(fieldId)?.name ?? fieldId} crude now runs to ${ref.name}${local ? ' by gravity line' : ''}.`);
}

export function sellProductImpl(state, refUid, pid, qty) {
  const ref = state.refineries?.[refUid];
  if (!ref) return no('No such refinery.');
  if (!REFINED_PRODUCTS.includes(pid) || !(pid in ref.products)) return no('Unknown product.');
  const asked = Math.min(Math.max(0, Number(qty) || 0), ref.products[pid]);
  if (asked < 1) return no(`No ${PRODUCTS[pid].name.toLowerCase()} in the tanks.`);
  const q = Math.min(asked, profitableQty(state, ref, pid));
  if (q < 1) return no(`The market for ${PRODUCTS[pid].name.toLowerCase()} is glutted today — nobody pays for the barrel. Hold, or dump it.`);
  const pr = sitePrice(state, ref, pid, q);
  if (pr.net <= 0) return no(`${PRODUCTS[pid].name} fetches less than its barrel (${fmt$(pr.gross * pr.depth)} vs ${fmt$(pr.pack)}). Hold or dump it.`);
  const net = sellNow(state, ref, pid, q, pr);
  return ok(`Sold ${Math.round(q).toLocaleString('en-US')} ${PRODUCTS[pid].unit} of ${PRODUCTS[pid].name.toLowerCase()} for ${fmt$(net)} net${q < asked - 0.5 ? ' — the market would take no more today' : ''}.`);
}

export function setProductSellModeImpl(state, refUid, pid, mode, pct = 100) {
  const ref = state.refineries?.[refUid];
  if (!ref) return no('No such refinery.');
  if (!REFINED_PRODUCTS.includes(pid) || !(pid in ref.products)) return no('Unknown product.');
  if (!['auto', 'hold', 'export', 'dump'].includes(mode)) return no('Mode must be auto, hold, export or dump.');
  if (mode === 'export' && (pid !== 'kerosene' || !siteDef(ref.siteId)?.export)) return no('Only kerosene from a New York works can be exported.');
  if (mode === 'export' && !flagOn(state, 'keroseneExport')) return no('No ships take oil cargo yet (first cargo sails Nov 1861).');
  if (mode === 'dump' && !PRODUCTS[pid].dumpable) return no(`You cannot pour ${PRODUCTS[pid].name.toLowerCase()} into the creek.`);
  normalizeRefinery(ref, yearOf(state));
  ref.sell[pid] = { mode, pct: clamp(Number(pct) || 100, 1, 100) };
  const fine = mode === 'dump' ? dumpingFine(state) : 0;
  return ok(`${PRODUCTS[pid].name}: ${mode === 'auto' ? `auto-sell ${ref.sell[pid].pct}%/day` : mode === 'dump' ? `run into the creek${fine ? ` (fined ${fmt$(fine)}/gal)` : ''}` : mode}.`);
}

export function setAutoBuyCrudeImpl(state, refUid, on, maxPrice = null, grade = null, fillPct = undefined) {
  const ref = state.refineries?.[refUid];
  if (!ref) return no('No such refinery.');
  normalizeRefinery(ref);
  const fp = fillPct === undefined ? ref.autoBuy.fillPct : fillPct == null ? undefined : Math.min(1, Math.max(0.1, Number(fillPct)));
  ref.autoBuy = { on: !!on, maxPrice: maxPrice != null ? Math.max(0, Number(maxPrice)) : ref.autoBuy.maxPrice, grade: grade ?? ref.autoBuy.grade ?? null, ...(fp != null ? { fillPct: fp } : {}) };
  return ok(on ? `${ref.name} ${fp != null ? `keeps its crude tanks ${Math.round(fp * 100)}% full,` : 'buys spot crude'} buying whenever crude is under ${fmt$(ref.autoBuy.maxPrice)}/bbl delivered.` : `${ref.name}: spot buying off.`);
}

export function setRefinerySafetyImpl(state, refUid, perDay) {
  const ref = state.refineries?.[refUid];
  if (!ref) return no('No such refinery.');
  ref.safetyBudget = Math.max(0, Number(perDay) || 0);
  return ok(`${ref.name} safety ${fmt$(ref.safetyBudget)}/day (−${Math.round(budgetMitigation(state, ref.safetyBudget) * 100)}% fires).`);
}

export function setBurnResiduumImpl(state, refUid, on) {
  const ref = state.refineries?.[refUid];
  if (!ref) return no('No such refinery.');
  ref.burnResiduum = !!on;
  return ok(on ? `${ref.name} fires its stills with its own residuum.` : `${ref.name} buys coal and sells its residuum.`);
}

// ================================================================ selectors (pure)
export function marginView(state, refUid, overrides = {}) {
  const ref = state.refineries?.[refUid];
  if (!ref) return null;
  const grade = overrides.grade ?? dominantGrade(state, ref);
  const fireTest = overrides.fireTest ?? ref.fireTest;
  const runB = Math.max(1, Math.min(ref.throughputBbl, refineryCapacity(ref) || ref.throughputBbl));
  const ch = yieldsAt(state, ref, grade, fireTest, runB);
  const yv = ch.y;
  const sourShare = grade === 'lima_sour' ? Math.max(0, 1 - treatCapacity(ref) / Math.max(1, ref.throughputBbl)) : 0;
  const q = { ...ref, kero: { flash: fireTest, skunk: sourShare }, gaso: ref.gaso ? { octane: ch.octane } : undefined };
  const by = {};
  let value = 0;
  for (const pid of plist(ref)) {
    const units = yv[pid] * GAL_PER_BBL * (pid === 'wax' ? WAX_LB_PER_GAL : 1);
    if (!units) { by[pid] = 0; continue; }
    const net = Math.max(0, siteGrossPrice(state, q, pid) - packagingPerUnit(state, pid));
    const mode = ref.sell[pid]?.mode;
    by[pid] = mode === 'dump' ? 0 : units * net;
    value += by[pid];
  }
  const quote = crudeQuote(state, ref, Math.max(1, ref.throughputBbl), grade);
  const own = Object.values(state.refining?.routes ?? {}).includes(ref.uid);
  const st = stillUnits(ref);
  const totCap = st.reduce((a, u) => a + u.capacityBbl, 0) || 1;
  let opex = 0, fuel = 0, residNeed = 0;
  for (const u of st) {
    const w = u.capacityBbl / totCap;
    opex += w * eraDollars(state, u.opexPerBbl, u.baseYear ?? u.year);
    fuel += w * eraDollars(state, u.fuelPerBbl, u.baseYear ?? u.year);
    residNeed += w * (u.residGalPerBbl ?? 1);
  }
  if (yf(state) > 1900 || Object.keys(ch.load).length) { const k = costMult(state); opex *= k; fuel *= k; const pc = processCosts(state, ref, ch.load, runB); opex += pc.opex / runB; fuel += pc.fuel / runB; }
  if (ref.burnResiduum) {
    const residMade = yv.residuum * GAL_PER_BBL;
    const used = Math.min(residNeed, residMade);
    fuel *= 1 - used / Math.max(1e-9, residNeed);
    const residNet = Math.max(0, siteGrossPrice(state, q, 'residuum'));
    value -= used * residNet; by.residuum = Math.max(0, by.residuum - used * residNet);
  }
  const fdef = unitDef('frasch_unit');
  const treated = grade === 'lima_sour' ? Math.min(1, treatCapacity(ref) / Math.max(1, ref.throughputBbl)) : 0;
  const license = treated && !state.research?.done?.frasch_desulfurization && yearOf(state) < fdef.patentUntil ? eraDollars(state, fdef.licenseFeePerBbl + fdef.opexPerBbl + fdef.fuelPerBbl, 1888) * treated : treated ? eraDollars(state, fdef.opexPerBbl + fdef.fuelPerBbl, 1888) * treated : 0;
  const lampCost = (ref.sell.kerosene?.mode === 'export' ? 0 : yv.kerosene * GAL_PER_BBL * lampRatePerGal(fireTest) * interp(LAMP_ERA_MULT, yf(state)) * eraDollars(state, (LAMP_SUIT.damages[0] + LAMP_SUIT.damages[1]) / 2 * 1.25, LAMP_SUIT.baseYear));
  const min = flagOn(state, 'fireTestLaw') ? legalFireTest(yearOf(state)) : null;
  const crude = quote.price;
  const runBbl = Math.max(1, Math.min(ref.throughputBbl, refineryCapacity(ref) || ref.throughputBbl));
  // expected cost of being caught under the law: fine + a day's kerosene condemned + shutDays of lost margin
  const gross = Math.max(0, value - crude - opex - fuel);
  const inspect = min && fireTest < min ? INSPECTION.chancePerDay * (eraDollars(state, INSPECTION.fine, INSPECTION.baseYear) / runBbl + INSPECTION.condemnPct * by.kerosene + (INSPECTION.shutDays ?? 0) * gross) : 0;
  const fixed = fixedCostPerDay(state, ref) / runBbl;
  const netPerBbl = value - crude - opex - fuel - license - lampCost - inspect - fixed;
  const ny = productPrices(state);
  const spread = crackSpread(yv, ny, Math.max(0, spotPrice(state)));
  return {
    grade, fireTest, yields: yv, byProduct: by, productValue: value, crudeDelivered: crude, crudePosted: quote.posted, crudeFreight: quote.freight,
    ownCrude: own, opex, fuel, fixed, license, lampCost, inspectCost: inspect, netPerBbl, perDay: netPerBbl * runBbl,
    crackSpread: spread.spread, nyProductValue: spread.value, crudeSpot: Math.max(0, spotPrice(state)), squeeze: squeezeState(state),
    octane: ch.octane, load: ch.load,
  };
}

function dominantGrade(state, ref) {
  let best = null, v = -1;
  for (const [g, b] of Object.entries(ref.crude)) if (b > v) { v = b; best = g; }
  if (v > 0.5) return best;
  const routed = Object.entries(state.refining?.routes ?? {}).find(([, u]) => u === ref.uid);
  if (routed) return gradeForField(routed[0]);
  return buyableGrades(state, ref)[0] ?? 'penn';
}

// Fire-test slider readouts (like the WOB card): yields, price, lamp risk and a margin curve.
export function fireTestView(state, refUid, f = null) {
  const ref = state.refineries?.[refUid];
  if (!ref) return null;
  const F = f ?? ref.fireTest;
  const grade = dominantGrade(state, ref);
  const yv = yieldsAt(state, ref, grade, F).y;
  const keroGalDay = yv.kerosene * GAL_PER_BBL * Math.min(ref.throughputBbl, refineryCapacity(ref) || ref.throughputBbl);
  const min = flagOn(state, 'fireTestLaw') ? legalFireTest(yearOf(state)) : null;
  const curve = [];
  for (let x = FIRE_TEST.min; x <= FIRE_TEST.max; x += 5) {
    const m = marginView(state, refUid, { fireTest: x, grade });
    curve.push({ f: x, margin: m.netPerBbl, risk: keroGalDay * 365 * lampRatePerGal(x) * interp(LAMP_ERA_MULT, yf(state)), kerosene: m.yields.kerosene });
  }
  return {
    fireTest: F, flashPoint: F - 10, grade, keroYield: yv.kerosene, naphthaYield: yv.naphtha, priceMult: fireTestPriceMult(F),
    lampRatePerMGal: lampRatePerGal(F) * 1e6, lampsPerYear: keroGalDay * 365 * lampRatePerGal(F) * interp(LAMP_ERA_MULT, yf(state)), stillFireMult: 1 + 0.6 * Math.max(0, (FIRE_TEST.default - F) / 40),
    legalMin: min, legal: !min || F >= min, exportOk: F >= EXPORT_MIN_FIRE_TEST, stops: FIRE_TEST.stops, min: FIRE_TEST.min, max: FIRE_TEST.max, curve,
  };
}

export function refineryView(state, refUid) {
  const ref = state.refineries?.[refUid];
  if (!ref) return null;
  const site = siteDef(ref.siteId);
  const counts = {};
  for (const id of ref.units) counts[id] = (counts[id] ?? 0) + 1;
  const units = Object.entries(counts).map(([id, n]) => ({ id, n, def: unitDef(id) }));
  const products = plist(ref).map((pid) => {
    const pr = sitePrice(state, ref, pid, 1);
    return { id: pid, name: PRODUCTS[pid].name, short: PRODUCTS[pid].short, unit: PRODUCTS[pid].unit, color: PRODUCTS[pid].color, qty: ref.products[pid],
      gal: pid === 'wax' ? ref.products[pid] / WAX_LB_PER_GAL : ref.products[pid], price: pr.gross, net: pr.net, pack: pr.pack,
      ny: productPrice(state, pid), mode: ref.sell[pid]?.mode ?? 'hold', pct: ref.sell[pid]?.pct ?? 100, dumpable: !!PRODUCTS[pid].dumpable,
      made: ref.today?.made?.[pid] ?? 0, sold: ref.today?.sold?.[pid] ?? 0, sellable: Math.min(ref.products[pid], profitableQty(state, ref, pid)),
      value: (() => { const q = Math.min(ref.products[pid], profitableQty(state, ref, pid)); if (q < 1) return 0; const p2 = sitePrice(state, ref, pid, q); return Math.max(0, p2.net) * q; })() };
  });
  const routes = Object.entries(state.refining?.routes ?? {}).filter(([, u]) => u === ref.uid).map(([fid]) => ({
    fieldId: fid, name: fieldDef(fid)?.name ?? fid, storage: state.fields?.[fid]?.storage?.bbl ?? 0,
    local: ref.siteId === 'field_side' && ref.fieldId === fid, grade: gradeForField(fid),
    haulCap: ref.siteId === 'field_side' && ref.fieldId === fid ? null : transportCapacity(state, fid),
  }));
  const routable = Object.keys(state.fields ?? {}).filter((fid) => state.fields[fid].plots?.some((p) => p.owned) || state.fields[fid].storage?.bbl > 0)
    .map((fid) => ({ fieldId: fid, name: fieldDef(fid)?.name ?? fid, routedTo: state.refining?.routes?.[fid] ?? null, grade: gradeForField(fid) }));
  const yNow = yearOf(state);
  const buildable = Object.values(REFINERY_UNITS).filter((u) => (u.m ?? 1) <= 1 && !(u.obsoleteYear && yNow >= u.obsoleteYear && !ref.units.includes(u.id))).map((u) => {
    const all = [...ref.units, ...ref.pending.map((p) => p.defId)];
    const avail = unitAvailable(state, u.id);
    const prereq = !u.prereqUnit || u.prereqUnit.some((p) => ref.units.includes(p));
    const room = all.filter((x) => x === u.id).length < (u.maxPerSite ?? 99);
    return { id: u.id, name: u.name, kind: u.kind, year: u.year, price: unitPriceOf(state, u.id, ref.siteId), capacityBbl: u.capacityBbl, treatBbl: u.treatBbl ?? 0, feed: u.feed ?? null,
      crudeStorageBbl: u.crudeStorageBbl, productStorageGal: u.productStorageGal, desc: u.desc, available: avail, prereq, room,
      ok: avail && prereq && room, why: !avail ? `from ${u.year}${u.tech ? ' (or R&D)' : ''}` : !prereq ? `needs ${u.prereqUnit.map((p) => unitDef(p)?.name).join(' / ')}` : !room ? 'site full' : '' };
  }).filter((b) => b.year <= yearOf(state) + 12 || b.available);
  const grades = buyableGrades(state, ref).map((g) => ({ ...crudeQuote(state, ref, Math.max(1, ref.throughputBbl), g), name: CRUDE_GRADES[g].name }));
  const offer = Object.values(state.refining?.standard?.offers ?? {}).find((o) => o.status === 'pending' && o.refineryUid === ref.uid) ?? null;
  const warnings = Object.values(state.safety?.warnings ?? {}).filter((w) => w.refineryUid === ref.uid);
  return {
    ref, site, units, pending: ref.pending.map((p) => ({ ...p, def: unitDef(p.defId), daysLeft: p.readyDay - state.day })),
    capacity: refineryCapacity(ref), treatCapacity: treatCapacity(ref), throughput: ref.throughputBbl, running: (ref.today?.run ?? 0) > 0,
    down: state.day < ref.downUntil, downDays: Math.max(0, ref.downUntil - state.day), fire: ref.fire && state.day <= ref.fire.untilDay ? ref.fire : null,
    crude: { total: crudeTotal(ref), cap: crudeCapacity(ref), byGrade: { ...ref.crude }, avgCost: crudeTotal(ref) > 0 ? ref.crudeCost / crudeTotal(ref) : 0 },
    productCap: productCapacity(ref), productUsed: storedGal(ref), products, kero: { ...ref.kero },
    yields: yieldsAt(state, ref, dominantGrade(state, ref)).y, grade: dominantGrade(state, ref), chain: chainView(state, ref),
    fireTest: fireTestView(state, refUid), margin: marginView(state, refUid), today: ref.today, history: ref.history,
    routes, routable, grades, autoBuy: { ...ref.autoBuy }, buildable, offer, squeeze: squeezeState(state), warnings,
    safety: { budget: ref.safetyBudget, mitigation: budgetMitigation(state, ref.safetyBudget), daysWithoutIncident: ref.daysWithoutIncident,
      fireChance: refineryIncidentChance(state, ref, REFINERY_ROWS[0]), explosionChance: refineryIncidentChance(state, ref, REFINERY_ROWS[1]) },
    shipments: (state.refining?.shipments ?? []).filter((s) => s.refineryUid === ref.uid).map((s) => ({ ...s, daysLeft: s.arriveDay - state.day })),
    shipCapGal: productShipCap(state, ref), canExport: !!site?.export && flagOn(state, 'keroseneExport'),
    exportPremium: exportPremium(yf(state)), oceanFreight: oceanFreight(yf(state)), legalFireTest: flagOn(state, 'fireTestLaw') ? legalFireTest(yearOf(state)) : null,
    value: replacementValue(state, ref),
  };
}

// Process chain readout for the flow diagram / 3D view / column cutaway: conversion units with their load,
// gasoline octane vs the market, and the straight-run cut of the crude unit.
export function chainView(state, ref) {
  const grade = dominantGrade(state, ref);
  const run = Math.max(1, Math.min(ref.throughputBbl, refineryCapacity(ref) || ref.throughputBbl));
  const ch = yieldsAt(state, ref, grade, ref.fireTest, run);
  const y = yf(state);
  const straight = applyFireTest(baseYields(ref, grade), ref.fireTest);
  const units = convGroups(ref).map((g) => ({ id: g.id, name: g.def.name, n: g.n, cap: g.cap, kind: g.def.kind, feed: g.def.feed, out: Object.keys(g.def.out), outFrac: { ...g.def.out }, octane: g.def.octane ?? null,
    load: ch.load[g.id] ?? 0, util: g.cap > 0 ? clamp((ch.load[g.id] ?? 0) / g.cap, 0, 1) : 0 }));
  return {
    grade, run, units, straight, octane: ch.octane, poolOctane: ch.octane + leadBoost(y), standard: y >= MOTOR_GASOLINE_YEAR ? octaneStandard(y) : null, lead: leadBoost(y),
    octaneMult: ref.gaso ? octanePriceMult(ch.octane, y) : 1, hydrotreat: hydroCap(ref), sulfurMult: dieselSulfurMult(state, ref),
    sru: ref.units.includes('sulfur_recovery'), emissions: y >= EMISSIONS_COST.fromYear && hasAny(ref, ['pipe_still', 'crude_unit']) && !ref.units.includes('sulfur_recovery'),
    modern: hasAny(ref, ['pipe_still', 'crude_unit']) || units.length > 0,
  };
}

export function refineriesView(state) {
  return Object.values(state.refineries ?? {}).map((r) => ({ uid: r.uid, name: r.name, siteId: r.siteId, site: siteDef(r.siteId)?.name, capacity: refineryCapacity(r), running: (r.today?.run ?? 0) > 0, fire: !!(r.fire && state.day <= r.fire.untilDay) }));
}

// Where a new refinery can go right now (for the "build" card).
export function refinerySitesView(state) {
  const y = yearOf(state);
  return M1_SITES.map((id) => {
    const s = siteDef(id);
    const fields = s.needsField ? Object.keys(state.fields ?? {}).filter((f) => state.fields[f].plots?.some((p) => p.owned)) : [];
    const taken = Object.values(state.refineries ?? {}).filter((r) => r.siteId === id).map((r) => r.fieldId ?? true);
    return { id, name: s.name, short: s.short, desc: s.desc, descByYear: s.descByYear ?? null, open: y >= s.fromYear, fromYear: s.fromYear, rail: s.rail, export: !!s.export,
      productMult: siteProductMult(state, id), crudeFreight: eraDollars(state, s.crudeFreight * fi(state), 1865), squeezeFreight: squeezeFreight(state, id),
      fields: fields.filter((f) => !taken.includes(f)).map((f) => ({ id: f, name: fieldDef(f)?.name ?? f })), taken: s.needsField ? false : taken.length > 0,
      starter: ['cheese_box_still', 'batch_still_house', 'refinery_works', 'pipe_still', 'crude_unit'].filter((u) => unitAvailable(state, u) && !(unitDef(u).obsoleteYear && y >= unitDef(u).obsoleteYear)).map((u) => ({ id: u, name: unitDef(u).name, price: unitPriceOf(state, u, id), capacityBbl: unitDef(u).capacityBbl })) };
  });
}

export function productMarketsView(state) {
  const pm = state.productMarkets ?? {};
  return productsAt(yf(state)).map((pid) => {
    const m = pm[pid];
    const h = m?.history ?? [];
    const yearAgo = h.find((p) => p[0] >= state.day - 365);
    const price = m?.price ?? productPrice(state, pid);
    return { id: pid, name: PRODUCTS[pid].name, short: PRODUCTS[pid].short, unit: PRODUCTS[pid].unit, color: PRODUCTS[pid].color, uses: PRODUCTS[pid].uses,
      price, base: m?.base ?? price, change1y: yearAgo && yearAgo[0] <= state.day - 300 ? price / yearAgo[1] - 1 : null, history: h,
      perBbl: price * GAL_PER_BBL * (pid === 'wax' ? WAX_LB_PER_GAL : 1) };
  });
}

// Net-worth contribution (add to finance.assetValue): plant at half replacement, inventory at 70%, Standard stock at par.
export function refiningAssetValue(state) {
  let v = 0;
  for (const ref of Object.values(state.refineries ?? {})) {
    v += replacementValue(state, ref) * 0.5;
    v += inventoryValue(state, ref) * 0.7;
  }
  v += state.refining?.shares?.value ?? 0;
  return v;
}

// Merge-ready incident rows for sim/safety.js INCIDENTS (scope 'refinery' rows are skipped by tickSafety).
export const REFINERY_INCIDENT_ROWS = REFINERY_ROWS;
